// components/customers/CustomerEditDialog.tsx
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  Save,
  X,
  User,
  Phone as PhoneIcon,
  MapPin,
  Ellipsis,
  Trash,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { customerSchema } from "@/api/types";
import type { Customer } from "@/types/customer-type";
import {
  useUpdateCustomer,
  useDeleteCustomer,
} from "@/services/customer-service";

type CustomerFormValues = z.infer<typeof customerSchema>;

type CustomerEditDialogProps = {
  open: boolean;
  onOpenChange: (val: boolean) => void;
  customer: Customer | null;
  onSaved?: (customerId: string) => void; // optional callback after save
};

export default function CustomerEditDialog({
  open,
  onOpenChange,
  customer,
  onSaved,
}: CustomerEditDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();
  // Initialize form with proper default values
  const defaultValues: CustomerFormValues = {
    name: customer?.name ?? "",
    phone: customer?.phone ?? "",
    address: customer?.address ?? "",
    saleChannel: customer?.saleChannel ?? "Facebook",
    notTransfer: customer?.notTransfer ?? false,
  };

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: defaultValues,
    mode: "onChange",
  });

  // Reset form when customer changes
  useEffect(() => {
    if (customer) {
      form.reset({
        name: customer.name ?? "",
        phone: customer.phone ?? "",
        address: customer.address ?? "",
        saleChannel: customer.saleChannel ?? "Facebook",
        notTransfer: customer.notTransfer ?? false,
      });
    }
  }, [customer, form]);

  // Submit
  const onSubmit = async (data: CustomerFormValues) => {
    if (!customer?.id) return;
    setIsSubmitting(true);
    try {
      await updateCustomerMutation.executeAsync({ id: customer.id, data });
      toast({
        title: "Customer updated",
        description: "Changes saved successfully.",
      });
      onSaved?.(customer.id);
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast({
        title: "Update failed",
        description: "Please check your inputs or try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete
  const onDelete = async () => {
    if (!customer?.id) return;
    try {
      await deleteCustomerMutation.executeAsync(customer.id);
      toast({
        title: "Customer deleted",
        description: "The record has been removed.",
      });
      setConfirmDeleteOpen(false);
      onOpenChange(false);
      // No onSaved here; parent typically refreshes list when dialog closes
    } catch (err) {
      console.error(err);
      toast({
        title: "Delete failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Keyboard shortcut: Cmd/Ctrl + S to submit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        form.handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, form, onSubmit]);

  const initials = useMemo(() => {
    const n = customer?.name?.trim() || "";
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  }, [customer?.name]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-xl p-0 overflow-hidden"
          aria-describedby={undefined}
        >
          {/* Sticky header */}
          <div className="sticky top-0 z-10 border-b bg-white">
            <DialogHeader className="px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                    {initials || <User className="h-4 w-4" />}
                  </div>
                  <div>
                    <DialogTitle className="text-base">
                      Edit Customer
                    </DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">
                      Update customer details and preferences
                    </DialogDescription>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Ellipsis className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuLabel>More</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-700"
                        onClick={() => setConfirmDeleteOpen(true)}
                      >
                        <Trash className="mr-2 h-4 w-4" /> Delete Customer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Scrollable body */}
          <div className="px-5 py-4 max-h-[65vh] overflow-y-auto">
            <Form {...form}>
              <form
                id="customer-edit-form"
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Customer Name<span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="e.g., Engleang Sam"
                              className="pl-10"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Phone */}
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <PhoneIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="e.g., 012 345 678"
                              className="pl-10"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-[11px]">
                          Contact number for delivery/updates
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Address */}
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Street, district, city"
                            className="pl-10"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-[11px]">
                        Used for delivery and receipts
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Sale Channel */}
                  <FormField
                    control={form.control}
                    name="saleChannel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sales Channel</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select channel" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Facebook">Facebook</SelectItem>
                            <SelectItem value="Instagram">Instagram</SelectItem>
                            <SelectItem value="Line">Line</SelectItem>
                            <SelectItem value="Website">Website</SelectItem>
                            <SelectItem value="Walk-in">Walk-in</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Not Transfer */}
                <FormField
                  control={form.control}
                  name="notTransfer"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Not Transfer</FormLabel>
                        <FormDescription className="text-[11px]">
                          Check if the customer hasn’t transferred payment yet
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>

          {/* Sticky footer */}
          <DialogFooter className="px-5 pb-5 pt-3 border-t bg-white flex items-center justify-between">
            <div className="text-[11px] text-muted-foreground">
              Tip: Press{" "}
              <kbd className="px-1 py-0.5 border rounded">⌘/Ctrl</kbd> +{" "}
              <kbd className="px-1 py-0.5 border rounded">S</kbd> to save
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="customer-edit-form"
                className="gap-2 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save changes</span>
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The customer and related records may
              be impacted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash className="mr-2 h-4 w-4" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
